import { fillText } from 'components/invoiceImport/invoiceImportText';

/**
 * What the "Commentaires" tab of an invoice's detail says (JBWEB-1279).
 * `text` reads the `invoiceComments` locale space.
 */

// The api's bound (COMMENT_TEXT_MAX_LENGTH), counted as it counts: the
// trimmed text's `length`, in UTF-16 code units (an emoji is 2).
export const COMMENT_MAX_LENGTH = 2000;
// The counter shows from here on, so that the limit is seen before it bites.
export const COMMENT_COUNTER_FROM = 1800;

export const commentLengthOf = (draft) => String(draft ?? '').trim().length;

/** Whether the api would take this text: 1 to 2 000 characters once trimmed. */
export const isCommentSendable = (draft) => {
  const length = commentLengthOf(draft);
  return length > 0 && length <= COMMENT_MAX_LENGTH;
};

const nameParts = (author) =>
  [author?.firstname, author?.lastname]
    .filter((part) => typeof part === 'string' && part.trim())
    .map((part) => part.trim());

/**
 * "Léa Martin"; a deleted account (`author: null`) or a user without a name
 * gets a stand-in, never an error.
 */
export const authorNameOf = (text, author) => {
  if (!author) return text('deletedAuthor', 'Compte supprimé');
  const parts = nameParts(author);
  return parts.length
    ? parts.join(' ')
    : text('unnamedAuthor', 'Auteur sans nom');
};

/** "LM" for Léa Martin, "?" when there is no name to take them from. */
export const authorInitialsOf = (author) => {
  const initials = nameParts(author)
    // By code point: a name may start with a character outside the BMP.
    .map((part) => [...part][0].toUpperCase())
    .join('');
  return initials || '?';
};

const PARIS = 'Europe/Paris';

/** "03/10/2026 à 14:32", in Paris time whatever the browser's zone. */
export const formatCommentAt = (text, value) => {
  const date = new Date(value);
  if (value == null || Number.isNaN(date.getTime())) return null;
  return fillText(text('at', '{date} à {time}'), {
    date: date.toLocaleDateString('fr-FR', {
      timeZone: PARIS,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('fr-FR', {
      timeZone: PARIS,
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
};

const SEND_ERROR_FALLBACKS = {
  invalid: 'Le commentaire doit faire de 1 à 2 000 caractères.',
  notFound:
    'Cette facture est introuvable : le commentaire n’a pas été enregistré.',
  failed: 'Le commentaire n’a pas pu être enregistré. Réessayez.',
};

/** The kind of a refused post: "invalid" (400), "notFound" (404) or "failed". */
export const sendFailureOf = (error) => {
  const errorCode = error?.response?.data?.errorCode;
  if (errorCode === 'COMMENT_INVALID') return 'invalid';
  if (errorCode === 'INVOICE_NOT_FOUND' || error?.response?.status === 404)
    return 'notFound';
  return 'failed';
};

export const sendErrorText = (text, failure) =>
  text(`errors.${failure}`, SEND_ERROR_FALLBACKS[failure]);
